import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { Box, Chip, Divider, Tab, Tabs } from "@mui/material";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import Loader from "../../UiComponents/Loader/Loader";
import { SearchIcon, AddIcon } from "../../Resources/Icons/icons";
import {
  GetPolicyWalletList,
  GetPolicyWalletBalance,
  GetMyPolicyWalletStatement,
  CreatePolicyWalletTopup,
  getReceiptImage,
} from "../../Service/_index";
import WalletTopupDialog from "../UserWallet/components/WalletTopupDialog";
import StatusResultDialog from "../UserWallet/components/StatusResultDialog";
import ImageViewerDialog from "../UserWallet/components/ImageViewerDialog";
import TopupSource from "../AddMoneyToUser/TopupSource";
import { formatINR, isPdfKey, proofKeyOf, proofNameOf } from "../AddMoneyToUser/addMoneyUtils";
import WalletStatementView from "../AdminPolicyWallet/WalletStatementView";
import { isAtOrBelowMinimum } from "../../Shared/walletMinimum";
import { requestWalletAlertsRefresh } from "../Notification/useWalletAlerts";
import MinimumBalanceDialog from "./MinimumBalanceDialog";
import wallett from "../../Resources/Images/wallet.png";

const getStatusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "REJECTED":
      return "red";
    default:
      return "orange";
  }
};

const UserPolicyWallet = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [openLoader, setOpenLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [walletTopupDialog, setWalletTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupFile, setTopupFile] = useState(null);
  const [amountError, setAmountError] = useState("");
  const [fileError, setFileError] = useState("");
  const [resultDialog, setResultDialog] = useState({ open: false, type: "", message: "" });
  const [imageViewDialog, setImageViewDialog] = useState({ open: false, imageUrl: "", fileName: "" });
  const [availableBalance, setAvailableBalance] = useState(0);
  // The low-balance threshold this user set for their own wallet (0 = none). Known only once the balance has loaded.
  const [minimumBalance, setMinimumBalance] = useState(0);
  const [minimumKnown, setMinimumKnown] = useState(false);
  const [minimumDialog, setMinimumDialog] = useState(false);
  // "statement": every rupee in and out (top-ups, policy debits, refunds); "requests": my top-up requests and their status.
  const [tab, setTab] = useState("statement");
  const [statementKey, setStatementKey] = useState(0); // a new key makes the statement load again

  const headCells = [
    { id: "sno", placeMent: true, disablePadding: false, label: "S.No", type: "index" },
    {
      id: "createdAt",
      placeMent: true,
      disablePadding: false,
      label: "Date",
      type: "component",
      renderComponent: (row) => (
        <Typography>{row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A") : "-"}</Typography>
      ),
    },
    {
      id: "type",
      placeMent: true,
      disablePadding: false,
      label: "Type",
      type: "component",
      renderComponent: (row) => <TopupSource row={row} />,
    },
    {
      id: "amount",
      placeMent: true,
      disablePadding: false,
      label: "Top Up Amount",
      type: "component",
      renderComponent: (row) => (
        <Typography sx={{ color: "green", fontWeight: "500" }}>{formatINR(row.amount)}</Typography>
      ),
    },
    {
      id: "proof",
      placeMent: false,
      disablePadding: false,
      label: "Proof",
      type: "component",
      renderComponent: (row) => {
        if (proofKeyOf(row.proof)) {
          return (
            <IconButton size="small" onClick={() => handleViewProof(row)} sx={{ color: "#1E3A5F" }}>
              <VisibilityIcon />
            </IconButton>
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      },
    },
    {
      id: "isApproved",
      placeMent: true,
      disablePadding: false,
      label: "Status",
      type: "coloredText",
      getColor: (row) => getStatusColor(row.isApproved),
    },
  ];

  const tableConfig = {
    isAction: false,
    isView: false,
    isEdit: false,
    isDelete: false,
    isSearch: false,
    isUpload: false,
    isFileDownload: false,
  };

  const GetData = () => {
    setOpenLoader(true);
    GetPolicyWalletList()
      .then((res) => {
        setOpenLoader(false);
        if (res && !res.error && Array.isArray(res)) {
          const dataWithIndex = res.map((row, index) => ({ ...row, __index__: index + 1 }));
          setRequests(dataWithIndex);
          setOriginalData(dataWithIndex);
        } else {
          setRequests([]);
          setOriginalData([]);
        }
        setPage(0);
      })
      .catch(() => {
        setOpenLoader(false);
        setRequests([]);
        setOriginalData([]);
      });
  };

  const GetBalance = () => {
    GetPolicyWalletBalance().then((res) => {
      setAvailableBalance(Number(res?.balance) || 0);
      setMinimumBalance(Number(res?.minimumBalance) || 0);
      setMinimumKnown(!res?.error);
    });
  };

  useEffect(() => {
    GetData();
    GetBalance();
  }, []);

  const onSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    if (searchTerm === "") {
      setRequests(originalData);
    } else {
      setRequests(
        originalData.filter((item) =>
          `${item.amount} ${item.isApproved}`.toLowerCase().includes(searchTerm)
        )
      );
    }
    setPage(0);
  };

  const handleViewProof = async (row) => {
    setOpenLoader(true);
    try {
      // Requests store {key, fileName}; admin top-ups store just the key.
      const key = proofKeyOf(row.proof);
      const response = await getReceiptImage(key);
      setOpenLoader(false);
      if (!response.error && response.url) {
        if (isPdfKey(key)) {
          window.open(response.url, "_blank", "noopener,noreferrer");
          return;
        }
        setImageViewDialog({
          open: true,
          imageUrl: response.url,
          fileName: proofNameOf(row.proof),
        });
      }
    } catch (error) {
      setOpenLoader(false);
    }
  };

  const handleWalletTopup = () => {
    setTopupAmount("");
    setTopupFile(null);
    setAmountError("");
    setFileError("");
    setWalletTopupDialog(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError("");

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError("File size must be less than 5MB");
        event.target.value = null;
        setTopupFile(null);
        return;
      }
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setFileError("Only images (JPEG, PNG, GIF) and PDF files are allowed");
        event.target.value = null;
        setTopupFile(null);
        return;
      }
      setTopupFile(file);
    }
  };

  const confirmWalletTopup = () => {
    setAmountError("");
    setFileError("");

    let hasError = false;
    const numAmount = Number(topupAmount);
    if (!topupAmount || isNaN(numAmount) || numAmount <= 0) {
      setAmountError("Please enter a valid amount");
      hasError = true;
    }
    if (!topupFile) {
      setFileError("Please upload a receipt/proof of payment");
      hasError = true;
    }

    if (hasError) return;

    setWalletTopupDialog(false);
    setOpenLoader(true);

    const formData = new FormData();
    formData.append("amount", numAmount);
    formData.append("file", topupFile);

    CreatePolicyWalletTopup(formData)
      .then((res) => {
        setOpenLoader(false);
        if (res && !res.error) {
          setResultDialog({ open: true, type: "success", message: res.message || "Topup request submitted" });
          GetData();
          GetBalance();
          setStatementKey((n) => n + 1);
        } else {
          setResultDialog({ open: true, type: "error", message: res?.message || "Failed to submit request" });
        }
      })
      .catch(() => {
        setOpenLoader(false);
        setResultDialog({ open: true, type: "error", message: "Failed to submit request" });
      });
  };

  const handleMinimumSaved = (saved) => {
    const value = Number(saved) || 0;
    setMinimumBalance(value);
    setMinimumDialog(false);
    requestWalletAlertsRefresh(); // the bell looks again now instead of within a minute
    setResultDialog({
      open: true,
      type: "success",
      message:
        value > 0
          ? `Saved. We will warn you in the bell when your balance is at or below ${formatINR(value)}.`
          : "Saved. The low-balance warning is turned off.",
    });
  };

  const atMinimum = minimumKnown && isAtOrBelowMinimum(availableBalance, minimumBalance);

  return (
    <>
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} className="Master_Card_Sub_Container">
          <Grid item xs={12} sm={6} md={6}>
            <Typography className="Master_Card_Tittle">Policy Wallet</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6} className="d-flex justify-content-end">
            <Button
              className="Master_Card_create_Button w-100"
              startIcon={<AddIcon sx={{ marginRight: "1.5px" }} />}
              onClick={handleWalletTopup}
            >
              Add Money
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
        </Grid>
        {minimumKnown && (
          <Grid item xs={12} sx={{ px: 2, pt: 1.5 }}>
            <Box
              role="region"
              aria-label="Minimum balance"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                px: 1.5,
                py: 1,
                border: "1px solid #EAECF0",
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
              }}
            >
              <NotificationsActiveIcon sx={{ fontSize: 20, color: "#667085" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "#667085", lineHeight: 1.3 }}>
                  Minimum balance
                </Typography>
                <Typography sx={{ fontFamily: "Inter", fontSize: "14px", fontWeight: 600, color: "#1E3A5F" }}>
                  {minimumBalance > 0 ? formatINR(minimumBalance) : "Not set"}
                </Typography>
              </Box>
              {atMinimum && (
                <Chip
                  size="small"
                  icon={<WarningAmberIcon sx={{ fontSize: "14px !important", color: "#B54708 !important" }} />}
                  label="At or below minimum"
                  sx={{
                    height: 24,
                    fontFamily: "Inter",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: "#B54708",
                    backgroundColor: "#FFFAEB",
                    border: "1px solid #FEDF89",
                  }}
                />
              )}
              <Typography sx={{ flex: 1, minWidth: 200, fontFamily: "Inter", fontSize: "12px", color: "#667085" }}>
                We warn you in the bell at the top of the page when your balance reaches it.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setMinimumDialog(true)}
                sx={{ textTransform: "none" }}
              >
                {minimumBalance > 0 ? "Edit" : "Set minimum"}
              </Button>
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sx={{ px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            aria-label="Policy wallet views"
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": { backgroundColor: "#1E3A5F" },
              "& .MuiTab-root": { textTransform: "none", fontFamily: "Inter", fontWeight: 500, minHeight: 44 },
              "& .Mui-selected": { color: "#1E3A5F !important" },
            }}
          >
            <Tab value="statement" label="Statement" />
            <Tab value="requests" label="Top-up Requests" />
          </Tabs>
        </Grid>
        {tab === "statement" ? (
          <Grid item xs={12}>
            <WalletStatementView
              key={statementKey}
              audience="user"
              fetchStatement={(args) => GetMyPolicyWalletStatement(args)}
              onOpenPolicy={(policyId) => navigate(`/onlinePolicy/view/form/${policyId}`)}
            />
          </Grid>
        ) : (
          <>
            <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container">
              <Grid
                item
                marginLeft={2}
                margin={1}
                sx={{
                  backgroundColor: "#F0F5FA",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  padding: "2px",
                  paddingRight: "15px",
                  paddingLeft: "15px",
                  gap: "10px",
                  border: "1px solid #DFE7F1",
                }}
              >
                <Grid>
                  <img src={wallett} width="18px" height="17px" alt="wallet" />
                </Grid>
                <Grid>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: "500",
                      color: "#1E3A5F",
                      fontSize: 16,
                      lineHeight: "1.2",
                    }}
                  >
                    {formatINR(availableBalance)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: "500",
                      color: "#667085",
                      fontSize: 12,
                      lineHeight: "1.2",
                    }}
                  >
                    Available Balance
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
                <Input
                  focused="false"
                  className="w-100 Master_Card_Input"
                  onChange={onSearch}
                  disableUnderline={true}
                  placeholder="Search in Policy Wallet"
                  startAdornment={
                    <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                      <IconButton aria-label="search">
                        <SearchIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <MasterCommonTable
                tableConfig={tableConfig}
                data={requests}
                columns={headCells}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                loading={openLoader}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Loader open={openLoader} />

      {minimumDialog && (
        <MinimumBalanceDialog
          currentMinimum={minimumBalance}
          balance={availableBalance}
          onClose={() => setMinimumDialog(false)}
          onSaved={handleMinimumSaved}
        />
      )}

      <WalletTopupDialog
        open={walletTopupDialog}
        onClose={() => setWalletTopupDialog(false)}
        topupAmount={topupAmount}
        setTopupAmount={setTopupAmount}
        topupFile={topupFile}
        handleFileChange={handleFileChange}
        onConfirm={confirmWalletTopup}
        amountError={amountError}
        fileError={fileError}
      />

      <StatusResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ open: false, type: "", message: "" })}
        type={resultDialog.type}
        message={resultDialog.message}
      />

      <ImageViewerDialog
        open={imageViewDialog.open}
        onClose={() => setImageViewDialog({ open: false, imageUrl: "", fileName: "" })}
        imageUrl={imageViewDialog.imageUrl}
        fileName={imageViewDialog.fileName}
      />
    </>
  );
};

export default UserPolicyWallet;
